import { useCallback, useEffect, useRef, useState } from "react";
import { getTaskLocalVariables, setTaskLocalVariables } from "../../services/camunda.service";
import { answerSignature, hasAnswer, parseAnswers } from "./assignmentDraft";

export default function useAssignmentDraft({ taskId, questionnaireId, userId, questions, answers, setAnswers, readOnly }) {
  const key = "teamassess:answers:" + userId + ":" + taskId;
  const [ready, setReady] = useState(false);
  const [baseline, setBaseline] = useState("");
  const [status, setStatus] = useState("loading");
  const [recovery, setRecovery] = useState(null);
  const [reload, setReload] = useState(0);
  const [storageFailed, setStorageFailed] = useState(false);
  const live = useRef({});
  const flight = useRef(null);
  const paused = useRef(false);
  const mounted = useRef(true);
  const dirty = ready && baseline !== answerSignature(questions, answers);
  live.current = { answers, questions, dirty, ready };
  const clearLocal = useCallback(() => { try { sessionStorage.removeItem(key); } catch {} }, [key]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; paused.current = true; };
  }, []);
  useEffect(() => {
    if (readOnly || !questions.length) return;
    let cancelled = false;
    paused.current = false;
    setReady(false);
    setStatus("loading");
    getTaskLocalVariables(taskId, { deserializeValues: false }, { silentError: true }).then(locals => {
      if (cancelled) return;
      const raw = locals.draftResponses || locals.answersDraft;
      const server = raw?.value ? parseAnswers(raw.value, questionnaireId, questions) : {};
      if (!server) throw new Error("Invalid server draft");
      setAnswers(server);
      setBaseline(answerSignature(questions, server));
      let local = null;
      try { local = parseAnswers(sessionStorage.getItem(key), questionnaireId, questions); } catch {}
      setRecovery(local && answerSignature(questions, local) !== answerSignature(questions, server) ? local : null);
      setReady(true);
      setStatus("saved");
    }).catch(() => { if (!cancelled) setStatus("load-error"); });
    return () => { cancelled = true; };
  }, [taskId, questionnaireId, key, questions, readOnly, reload, setAnswers]);

  useEffect(() => {
    if (!ready || readOnly || recovery) return;
    try {
      if (dirty) sessionStorage.setItem(key, JSON.stringify({ questionnaireId, answers }));
      else sessionStorage.removeItem(key);
      setStorageFailed(false);
    } catch { setStorageFailed(dirty); }
  }, [ready, readOnly, recovery, dirty, answers, key, questionnaireId]);

  const save = useCallback(async () => {
    if (!live.current.ready || readOnly || paused.current || recovery) return false;
    while (flight.current) {
      await flight.current;
      if (paused.current) return false;
    }
    if (!live.current.dirty) return true;
    const snapshot = live.current.answers;
    const currentQuestions = live.current.questions;
    const signature = answerSignature(currentQuestions, snapshot);
    setStatus("saving");
    const operation = (async () => {
      try {
        await setTaskLocalVariables(taskId, {
          draftResponses: { value: JSON.stringify({ questionnaireId, answers: snapshot, updatedAt: new Date().toISOString() }), type: "String" },
          progressMeter: { value: currentQuestions.filter(q => hasAnswer(q, snapshot[q.id])).length + "/" + currentQuestions.length, type: "String" },
        }, { silentError: true });
        if (mounted.current) {
          setBaseline(signature);
          setStatus("saved");
        }
        return true;
      } catch {
        if (mounted.current) setStatus("error");
        return false;
      }
    })();
    flight.current = operation;
    const result = await operation;
    if (flight.current === operation) flight.current = null;
    return result;
  }, [taskId, questionnaireId, readOnly, recovery]);

  useEffect(() => {
    if (!dirty || readOnly || recovery || status === "saving" || paused.current) return;
    const timer = setTimeout(save, status === "error" ? 15000 : 1500);
    return () => clearTimeout(timer);
  }, [dirty, answers, readOnly, recovery, status, save]);

  useEffect(() => {
    if ((!dirty && status !== "saving") || readOnly) return;
    const guard = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty, status, readOnly]);

  return {
    ready, dirty, status, recovery, storageFailed, save,
    retry: () => setReload(value => value + 1),
    recover: () => { setAnswers(recovery); setRecovery(null); },
    discardRecovery: () => { clearLocal(); setRecovery(null); },
    pause: async () => { paused.current = true; if (flight.current) await flight.current; },
    backup: snapshot => {
      try { sessionStorage.setItem(key, JSON.stringify({ questionnaireId, answers: snapshot })); }
      catch { setStorageFailed(true); }
    },
    resume: () => { paused.current = false; setStatus("error"); },
    complete: () => { paused.current = true; clearLocal(); setBaseline(answerSignature(questions, answers)); },
  };
}
